import { Stack } from "@volca/ui/components/ui/stack";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@volca/ui/components/ui/toggle-group";
import { cn } from "@volca/ui/lib/utils";
import { judge } from "@volca/ui/trades";
import { useReportCurrency } from "@/profitability/currency";
import { MissingNote, showValue } from "@/profitability/missing-value";
import { formatSummaryValue } from "@/profitability/utils";
import { formatChange } from "./overview-figures";
import type { OverviewFigure } from "./types";

/** Headline order comes from the script; selecting one changes its emphasis and history. */
export const OverviewMetrics = ({
  figures,
  selected,
  onSelect,
  compared,
}: {
  figures: OverviewFigure[];
  selected: OverviewFigure;
  onSelect: (key: string) => void;
  compared: boolean;
}) => {
  const currency = useReportCurrency();
  const context = (figure: OverviewFigure) => (
    <span className="flex min-h-4 flex-wrap items-center gap-x-2 gap-y-1 text-xs tabular-nums">
      {compared && figure.change !== null && figure.change !== 0 && (
        <span
          className={cn({
            "text-success": judge(figure.change, figure.better) === "good",
            "text-danger": judge(figure.change, figure.better) === "bad",
          })}
        >
          {formatChange(figure, currency)}
        </span>
      )}
      <MissingNote count={figure.missing} label={figure.label} />
    </span>
  );
  return (
    <Stack gap={5}>
      {figures.length > 1 ? (
        <ToggleGroup
          aria-label="Performance metric"
          value={[selected.key]}
          onValueChange={(next) => {
            if (next[0]) onSelect(next[0]);
          }}
          size="sm"
          className="w-full flex-wrap gap-1"
        >
          {figures.map((figure) => (
            <ToggleGroupItem
              key={figure.key}
              value={figure.key}
              className="h-auto min-h-9 min-w-0 px-2.5 py-2 whitespace-normal transition-[color,background-color,box-shadow] pointer-coarse:min-h-11"
            >
              <span className="sr-only">Show </span>
              {figure.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      ) : (
        <span className="text-sm text-subtle">{selected.label}</span>
      )}
      <Stack render={<dl />} gap={2}>
        <dt className="sr-only">{selected.label}</dt>
        <dd className="text-4xl leading-tight font-medium [overflow-wrap:anywhere] text-ink tabular-nums @sm/performance:text-5xl">
          {showValue(formatSummaryValue(selected, currency))}
        </dd>
        <dd>{context(selected)}</dd>
      </Stack>
      {figures.length > 1 && (
        <Stack
          render={<dl />}
          className="grid grid-cols-2 gap-x-5 gap-y-4 border-t border-line pt-4 @sm/performance:grid-cols-3"
        >
          {figures
            .filter((figure) => figure.key !== selected.key)
            .map((figure) => (
              <Stack key={figure.key} gap={1} className="min-w-0">
                <dt className="text-xs [overflow-wrap:anywhere] text-subtle">
                  {figure.label}
                </dt>
                <dd className="text-lg leading-snug font-medium [overflow-wrap:anywhere] text-ink tabular-nums">
                  {showValue(formatSummaryValue(figure, currency))}
                </dd>
                <dd>{context(figure)}</dd>
              </Stack>
            ))}
        </Stack>
      )}
    </Stack>
  );
};
